import React, { useState, useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';
import types from '../../redux/types';

import CustomInput from '../customInput';
import Timezone from '../timezone';
import ReportSettings from './reportSettings';

import API from '../../services/api';
import utils from '../../utils';
import reportUtils from '../../utils/report';
import calendar from '../../utils/calendar';

const frequencies = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const ReportInfo = ({ report, loadReports, project, registerSave }) => {
  const dispatch = useDispatch();

  const [email, setEmail] = useState(report.email);
  const [newReport, setNewReport] = useState(report);
  const [name, setName] = useState(report.name);
  const [timezone, setTimezone] = useState(report.timezone);
  const [errors, setErrors] = useState({});
  // const [reportSolution, setReportSolution] = useState({});

  useEffect(() => {
    setName(report.name);
    setEmail(report.email);
    setTimezone(report.timezone);
    setNewReport(report);
    // setReportSolution({
    //   id: String(report.table).toLowerCase()
    // });
  }, [report]);

  useEffect(() => {
    const validations = {
      email: [
        { key: 'required', value: true },
        { key: 'email', value: true }
      ],
      name: [{ key: 'required', value: true }]
    };
    setErrors({
      'email-input': (Array.isArray(email) && email.length ? email : [email])
        .flatMap(recipient => utils.fieldValidation(recipient, validations['email'])).join(', '),
      'name-input': utils.fieldValidation(name, validations['name']).join(', ')
    });
  }, [email, name]);

  const displayAlertMessage = (message, type) => {
    dispatch({
      type: types.SET_ALERT,
      payload: utils.generateAlert(message, type)
    });
  };

  const saveChanges = () => {
    const { interval } = newReport;

    // Merge errors from info and settings forms
    const errorList = {
      ...errors,
      ...newReport.errors
    };
    const monthlyDay = Number(newReport.daysToSend?.[0]);
    if (newReport.frequency === 'monthly' && (!Number.isInteger(monthlyDay) || monthlyDay < 1 || monthlyDay > 31)) {
      errorList['days-input'] = 'Choose a whole day from 1 to 31';
    }

    if (!utils.hasErrorsCheck(errorList)) {
      const { projectId, id } = newReport;
      const updateReportObject = reportUtils.generateUpdateReportObject({
        ...newReport,
        interval: String(interval.name).toUpperCase(),
        speedUnit: project.preferences?.speedUnit || ''
      });

      return API.updateReport(projectId, id, updateReportObject)
        .then(() => {
          displayAlertMessage('Report updated successfully', 'success');
          loadReports(projectId);
          return true;
        })
        .catch(error => {
          displayAlertMessage(error.message, 'error');
          return false;
        });
    } else {
      displayAlertMessage('Something is wrong. Any missing fields?', 'error');
      return false;
    }
  };

  const cancelChanges = () => {
    setName(report.name);
    setEmail(report.email);
    setTimezone(report.timezone);
    setNewReport(report);
  };

  // If parent wants to control saving/cancelling (e.g., modal Confirm/Cancel), register handlers
  const registerNotifyRef = useRef(null);
  useEffect(() => {
    if (typeof registerSave !== 'undefined' && registerSave) {
      const unregister = registerSave(notify => {
        registerNotifyRef.current = notify;

        const reg = {
          save: saveChanges,
          cancel: cancelChanges,
          hasChanges: () => {
            // Determine if report has changes compared to newReport
            const basicChanged =
              (report && report.name !== newReport.name) ||
              (report && report.email !== newReport.email) ||
              (report && report.timezone !== newReport.timezone);
            const deepChanged = !utils.compareObjects(report || {}, newReport || {});
            return !!basicChanged || !!deepChanged;
          }
        };

        // notify initial state
        notify(reg.hasChanges());

        return reg;
      });

      return () => {
        registerNotifyRef.current = null;
        if (typeof unregister === 'function') unregister();
      };
    }
  }, [registerSave, report, newReport, errors]);

  // Notify modal when internal change state updates
  useEffect(() => {
    if (registerNotifyRef.current) {
      registerNotifyRef.current(!utils.compareObjects(report || {}, newReport || {}));
    }
  }, [newReport, errors, report]);

  if (report) {
    return (
      <div className="report-info">
        {/* <EditButton
          isEditable={true}
          saveChanges={saveChanges}
          cancelChanges={cancelChanges}
          renderSmallButton
          changeEditModeState={changeEditModeState}
        /> */}

        <div className="d-flex flex-column justify-content-between input-column">
          <div className="form-group">
            <label htmlFor="name-input">Name</label>
            <CustomInput
              id="name-input"
              type="name"
              value={name}
              errorMessage={errors['name-input']}
              isEditable={true}
              onChange={e => {
                const { value } = e.target;
                setNewReport({ ...newReport, name: value });
                setName(value);
              }}
            />
          </div>
        </div>

        <div className="d-flex flex-column justify-content-between input-column">
          <div className="form-group">
            <label htmlFor="email-input">E-mail</label>
            <CustomInput
              id="email-input"
              type="email"
              value={email}
              onChange={e => {
                const { value } = e.target;
                setNewReport({ ...newReport, email: value });
                setEmail(value);
              }}
              isCopyButtonEnabled={true}
              errorMessage={errors['email-input']}
              isEditable={true}
            />
          </div>
        </div>

        <ReportSettings
          report={newReport}
          // solution={reportSolution}
          frequencies={frequencies}
          customIntervals={[{ id: 'minute', name: 'Minute' }, ...calendar.defaultIntervals]}
          onChange={updatedReport => setNewReport(updatedReport)}
          isEditable={true}
        />

        <div className="form-group">
          <label>Timezone</label>
          <Timezone
            disabled={false}
            selected={{ label: timezone, value: timezone }}
            onSelect={tz => {
              setNewReport({ ...newReport, timezone: tz });
              setTimezone(tz);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="no-data-message">
      <h1>No data found</h1>
    </div>
  );
};

export default ReportInfo;
